import { useCallback } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { GET_CONVERSATIONS_WITH_CONTACTS_QUERY } from '@zaplify/graphql';

/**
 * Encapsulates the logic for fetching conversations in a paginated fashion while
 * maintaining a single aggregated list. Resets automatically when the `where`
 * filter clause changes.
 */
export const usePaginatedConversations = (where: Record<string, unknown>, pageSize = 25) => {
    /* --------------------------------------------------------------------- */
    /* Apollo                                                                */
    /* --------------------------------------------------------------------- */
    const { data, loading, fetchMore, networkStatus } = useQuery(GET_CONVERSATIONS_WITH_CONTACTS_QUERY, {
        variables: { limit: pageSize, offset: 0, where },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    });

    const conversations = data?.Conversations ?? [];

    const isInitialLoading = loading && networkStatus === NetworkStatus.loading;
    const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

    // A simple heuristic: if the last page we received had < pageSize items we assume there's no more.
    // This works because our query always requests `limit = pageSize`.
    const hasMore = !isInitialLoading && !isFetchingMore && conversations.length % pageSize === 0;

    /* --------------------------------------------------------------------- */
    /* Pagination                                                             */
    /* --------------------------------------------------------------------- */
    const loadMore = useCallback(async () => {
        if (!hasMore || isFetchingMore) return;

        await fetchMore({
            variables: {
                limit: pageSize,
                offset: conversations.length,
                where,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previousResult;

                const merged = [
                    ...(previousResult.Conversations ?? []),
                    ...(fetchMoreResult.Conversations ?? []).filter(
                        (c) => !(previousResult.Conversations ?? []).some((p) => p.id === c.id)
                    ),
                ];

                return {
                    ...fetchMoreResult,
                    Conversations: merged,
                };
            },
        });
    }, [hasMore, isFetchingMore, fetchMore, conversations.length, where, pageSize]);

    return {
        conversations,
        isInitialLoading,
        isFetchingMore,
        hasMore,
        loadMore,
    } as const;
};
